/**
 * SAAS-2 · 订阅版顶栏工具区（仅个人站：useEdition() === "subscription" 才渲染；企业版 = 公司站这里返回 null，一个像素不变）。
 * 契约：designs/mockups/theme/个人站订阅版-样稿.html ②（tag saas-mockup-v1）：多端预览 ▾ · 帮助教程 · 公告 / 更新说明（红点）· 新订单提醒开关。
 * 顶部导航（TopNavLayout）和侧栏（Topbar）两种布局都挂这一个组件。
 *
 * · 多端预览：收拢已有功能，不新造——手机站预览 = 上架中心「手机站」预览台（LISTCTR-2）；以同行身份预览 = PPR-3 的 PartnerPreviewPanel；
 *   复制手机站地址 / 二维码 = 系统设置「手机站地址」（SAAS-2b）+ 前端二维码（qrcode.react，仓库已有）。每一项按原功能的权限出，没有可出的项整个按钮不出。
 * · 帮助教程：GET /subscription/toolbar 的 help_center_url；空 = 不显示（不放假链接）。
 * · 公告 / 更新说明：GET /release-notes?limit=3；红点 = unread_count > 0；点一条 = 标已读并进列表页。
 * · 新订单提醒：GET/PATCH /me/preferences（每人一个开关，跟账号走）；开着时收 /ws 的 order.created → 弹提示 + 提示音（用户有过页面交互才响）。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ChevronDown, CircleHelp, Copy, Handshake, Link2, Megaphone, MonitorSmartphone, ShoppingCart, Smartphone, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCan } from "@/auth/permissions";
import { useEdition } from "@/theme/useTheme";
import { PartnerPreviewPanel } from "@/pages/listing/PartnerSiteTab";
import { orderSourceLabel } from "@/pages/orders/orderConstants";
import { ORDER_CREATED_EVENT, SUB_QK, subscriptionApi, type OrderCreatedDetail, type ReleaseNote } from "@/services/subscription";

export function SubscriptionTools() {
  return useEdition() === "subscription" ? <Tools /> : null;
}

const tbtn = "relative flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-[12.5px] text-slate-600 hover:bg-brand-soft hover:text-brand";

/**
 * 下拉菜单定位：顶部导航的 header 是 overflow-x-auto（窄屏横向滚），绝对定位的下拉会被它裁掉——
 * 所以按按钮位置用 fixed 定位（右对齐按钮），滚动 / 改窗口大小时收起。
 */
function useAnchor(open: boolean, close: () => void) {
  const btn = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  useEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    const onMove = () => close();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, close]);
  return { btn, style: pos ? { position: "fixed" as const, top: pos.top, right: pos.right } : { position: "fixed" as const, visibility: "hidden" as const } };
}

/** 点外面收起（同 TopNavLayout「更多」的做法）。 */
function useOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, close]);
  return ref;
}

function Tools() {
  const toolbar = useQuery({ queryKey: SUB_QK.toolbar, queryFn: subscriptionApi.toolbar, staleTime: 5 * 60_000 });
  const help = toolbar.data?.help_center_url ?? null;
  return (
    <div className="flex shrink-0 items-center gap-1" data-testid="subscription-tools">
      <PreviewMenu mobileUrl={toolbar.data?.mobile_site_url ?? null} />
      {help && (
        <a href={help} target="_blank" rel="noopener noreferrer" title="帮助教程" aria-label="帮助教程" className={tbtn} data-testid="tool-help">
          <CircleHelp className="h-4 w-4" />
        </a>
      )}
      <ReleaseNotesMenu />
      <NewOrderAlert />
    </div>
  );
}

// ───────────────────────────── 多端预览 ─────────────────────────────

function MenuItem({ icon, title, sub, onClick, testid }: { icon: React.ReactNode; title: string; sub: string; onClick: () => void; testid: string }) {
  return (
    <button type="button" role="menuitem" onClick={onClick} data-testid={testid} className="flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-brand-soft">
      <span className="mt-0.5 text-brand">{icon}</span>
      <span className="min-w-0">
        <b className="block text-[13px] font-semibold text-slate-900">{title}</b>
        <span className="block text-[12px] text-slate-400">{sub}</span>
      </span>
    </button>
  );
}

function PreviewMenu({ mobileUrl }: { mobileUrl: string | null }) {
  const navigate = useNavigate();
  const canSite = useCan("listing.mappings.view");       // 手机站预览令牌的权限（LISTCTR-2）
  const canPartner = useCan("listing.mappings.manage");  // 以同行身份预览的权限（PPR-3）
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<"partner" | "qr" | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const ref = useOutside(open, close);
  const anchor = useAnchor(open, close);
  if (!canSite && !canPartner && !mobileUrl) return null;
  const pick = (fn: () => void) => () => { setOpen(false); fn(); };
  return (
    <div className="relative" ref={ref}>
      <button ref={anchor.btn} type="button" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen((v) => !v)} className={cn(tbtn, open && "bg-brand-soft text-brand")} data-testid="tool-preview" title="多端预览">
        <MonitorSmartphone className="h-4 w-4" /> 预览 <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div role="menu" style={anchor.style} className="z-40 flex w-[290px] flex-col gap-0.5 rounded-[10px] border border-slate-200 bg-white p-2 shadow-lg animate-in fade-in slide-in-from-top-1" data-testid="preview-menu">
          <div className="px-2.5 pb-0.5 pt-1.5 text-[11.5px] text-slate-400">多端预览</div>
          {canSite && <MenuItem testid="preview-site" icon={<Smartphone className="h-4 w-4" />} title="手机站预览" sub="预览模式：能点能滑，不下单不占座" onClick={pick(() => navigate("/admin/listing?tab=mobile"))} />}
          {canPartner && <MenuItem testid="preview-partner" icon={<Handshake className="h-4 w-4" />} title="以同行身份预览同行站" sub="选一个同行账号，2 小时只读" onClick={pick(() => setDialog("partner"))} />}
          {mobileUrl && <MenuItem testid="preview-qr" icon={<Link2 className="h-4 w-4" />} title="复制手机站地址 / 二维码" sub="发给客人或自己手机扫" onClick={pick(() => setDialog("qr"))} />}
        </div>
      )}
      {dialog === "partner" && (
        <Dialog title="以同行身份预览同行站" onClose={() => setDialog(null)}>
          <PartnerPreviewPanel />
        </Dialog>
      )}
      {dialog === "qr" && mobileUrl && (
        <Dialog title="手机站地址 / 二维码" onClose={() => setDialog(null)}>
          <SiteQr url={mobileUrl} />
        </Dialog>
      )}
    </div>
  );
}

export function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm animate-in fade-in" role="dialog" aria-label={title}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} aria-label="关闭" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SiteQr({ url }: { url: string }) {
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied("ok");
    } catch {
      setCopied("fail");
    }
  };
  return (
    <div className="flex flex-col gap-3" data-testid="site-qr">
      <div className="flex justify-center rounded-lg border border-slate-200 p-3"><QRCodeSVG value={url} size={200} /></div>
      <div className="flex items-center gap-2">
        <input readOnly value={url} aria-label="手机站地址" className="min-w-0 flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-[13px] text-slate-700" onFocus={(e) => e.currentTarget.select()} />
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:bg-brand-hover"><Copy size={13} />复制</button>
      </div>
      {copied === "ok" && <p className="text-[12px] text-emerald-600">已复制</p>}
      {copied === "fail" && <p className="text-[12px] text-rose-600">浏览器不让自动复制，请选中上面的地址手动复制</p>}
      <p className="text-[12px] leading-relaxed text-slate-400">这是对客的正式地址（系统设置 → 通用设置「手机站地址」），可以发给客人；客人在这里下单是真订单。</p>
    </div>
  );
}

// ───────────────────────────── 公告 / 更新说明 ─────────────────────────────

export function noteDate(n: Pick<ReleaseNote, "published_at" | "created_at">): string {
  const d = new Date(n.published_at ?? n.created_at);
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function ReleaseNotesMenu() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useOutside(open, close);
  const anchor = useAnchor(open, close);
  const q = useQuery({ queryKey: [...SUB_QK.notes, 3], queryFn: () => subscriptionApi.notes(3), staleTime: 60_000, refetchInterval: 10 * 60_000 });
  const unread = q.data?.unread_count ?? 0;
  const refresh = () => qc.invalidateQueries({ queryKey: SUB_QK.notes });
  const readOne = useMutation({ mutationFn: subscriptionApi.readNote, onSettled: refresh });
  const readAll = useMutation({ mutationFn: subscriptionApi.readAll, onSettled: refresh });
  const openNote = (n: ReleaseNote) => {
    setOpen(false);
    if (!n.is_read) readOne.mutate(n.id);
    navigate(`/release-notes#note-${n.id}`);
  };
  return (
    <div className="relative" ref={ref}>
      <button ref={anchor.btn} type="button" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen((v) => !v)} className={cn(tbtn, open && "bg-brand-soft text-brand")} title="公告 / 更新说明" aria-label="公告 / 更新说明" data-testid="tool-notes">
        <Megaphone className="h-4 w-4" />
        {unread > 0 && <span className="absolute right-[3px] top-[3px] h-[7px] w-[7px] rounded-full bg-accent" data-testid="notes-dot" />}
      </button>
      {open && (
        <div role="menu" style={anchor.style} className="z-40 flex w-[320px] flex-col gap-0.5 rounded-[10px] border border-slate-200 bg-white p-2 shadow-lg animate-in fade-in slide-in-from-top-1" data-testid="notes-menu">
          <div className="flex items-center justify-between px-2.5 pb-0.5 pt-1.5 text-[11.5px] text-slate-400">
            <span>公告 / 更新说明</span>
            {unread > 0 && <button type="button" onClick={() => readAll.mutate()} className="text-brand hover:underline">全部已读</button>}
          </div>
          {q.isError ? (
            <button type="button" onClick={() => q.refetch()} className="rounded-md px-2.5 py-3 text-left text-[13px] text-rose-600 hover:bg-rose-50">加载失败，点击重试</button>
          ) : q.isLoading ? (
            <p className="px-2.5 py-3 text-[13px] text-slate-400">加载中…</p>
          ) : (q.data?.items.length ?? 0) === 0 ? (
            <p className="px-2.5 py-3 text-[13px] text-slate-400">还没有更新说明</p>
          ) : (
            q.data!.items.map((n) => (
              <button key={n.id} type="button" role="menuitem" onClick={() => openNote(n)} data-testid="notes-item" className="flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-brand-soft">
                {n.is_read ? (
                  <span className="mt-0.5 shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{noteDate(n)}</span>
                ) : (
                  <span className="mt-0.5 shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent-hover">新</span>
                )}
                <span className="min-w-0">
                  <b className="block truncate text-[13px] font-semibold text-slate-900">{n.title}</b>
                  <span className="block truncate text-[12px] text-slate-400">{n.is_read ? n.body : `${noteDate(n)}${n.body ? ` · ${n.body}` : ""}`}</span>
                </span>
              </button>
            ))
          )}
          <Link to="/release-notes" onClick={() => setOpen(false)} className="rounded-md px-2.5 py-2 text-[13px] text-brand hover:bg-brand-soft" data-testid="notes-all">查看全部更新说明 ›</Link>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────── 新订单提醒 ─────────────────────────────

/** 浏览器限制：用户在页面上点过 / 按过键之后才允许出声。 */
let userInteracted = false;
if (typeof window !== "undefined") {
  const mark = () => { userInteracted = true; };
  window.addEventListener("pointerdown", mark, { once: true, capture: true });
  window.addEventListener("keydown", mark, { once: true, capture: true });
}
function canPlaySound(): boolean {
  const ua = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
  return userInteracted || !!ua?.hasBeenActive;
}

/** 提示音：Web Audio 生成两声短「叮」（不引入音频文件 / 新库）；不能出声就安静跳过。 */
export function playChime(): boolean {
  if (!canPlaySound()) return false;
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return false;
    const ctx = new Ctx();
    [0, 0.18].forEach((t, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = i === 0 ? 880 : 1175;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.16);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + 0.18);
    });
    setTimeout(() => ctx.close().catch(() => undefined), 600);
    return true;
  } catch {
    return false;
  }
}

interface Toast { id: number; detail: OrderCreatedDetail }
const TOAST_MS = 8000;

function NewOrderAlert() {
  const canOrders = useCan("orders.orders.list");
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: SUB_QK.prefs, queryFn: subscriptionApi.prefs, enabled: canOrders, staleTime: 5 * 60_000 });
  const on = !!prefs.data?.new_order_alert;
  const save = useMutation({
    mutationFn: (v: boolean) => subscriptionApi.setPrefs({ new_order_alert: v }),
    onSuccess: (d) => qc.setQueryData(SUB_QK.prefs, d),
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    if (!canOrders || !on) return;
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent<OrderCreatedDetail>).detail;
      if (!detail || !detail.count) return;
      const id = ++seq.current;
      setToasts((t) => [...t.slice(-2), { id, detail }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), TOAST_MS);
      playChime();
      qc.invalidateQueries({ queryKey: ["assistant"] });
    };
    window.addEventListener(ORDER_CREATED_EVENT, onEvt);
    return () => window.removeEventListener(ORDER_CREATED_EVENT, onEvt);
  }, [canOrders, on, qc]);

  if (!canOrders) return null;
  const busy = prefs.isLoading || save.isPending;
  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="新订单提醒"
        title={on ? "新订单提醒：开（来新订单弹提示 + 提示音）" : "新订单提醒：关"}
        disabled={busy || prefs.isError}
        onClick={() => save.mutate(!on)}
        className="ml-1 inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-slate-600 disabled:opacity-50"
        data-testid="tool-alert"
      >
        提醒
        <span className={cn("relative inline-block h-[18px] w-8 rounded-full transition-colors", on ? "bg-brand" : "bg-slate-300")}>
          <span className={cn("absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all", on ? "right-[2px]" : "left-[2px]")} />
        </span>
      </button>
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex w-[320px] flex-col gap-2" aria-live="polite" data-testid="order-toasts">
          {toasts.map((t) => {
            const first = t.detail.orders[0];
            const many = t.detail.count > 1;
            const to = !many && first?.order_id ? `/orders/${first.order_id}` : "/orders";
            return (
              <div key={t.id} role="status" className="flex items-start gap-2.5 rounded-[10px] border border-slate-200 bg-white p-3 shadow-lg animate-in fade-in slide-in-from-bottom-4" data-testid="order-toast">
                <span className="mt-0.5 rounded-md bg-accent-soft p-1.5 text-accent"><ShoppingCart className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <b className="block text-[13px] font-semibold text-slate-900">{many ? `来了 ${t.detail.count} 个新订单` : "来了新订单"}</b>
                  <span className="block truncate text-[12px] text-slate-500">
                    {many ? t.detail.orders.map((o) => o.order_no).join("、") + (t.detail.count > t.detail.orders.length ? " …" : "") : `${first?.order_no ?? ""} · ${orderSourceLabel(first?.source)}`}
                  </span>
                  <Link to={to} onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))} className="mt-1 inline-block text-[12px] font-medium text-brand hover:underline">去看看 ›</Link>
                </div>
                <button type="button" aria-label="关闭提醒" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))} className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><X size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
